/**
 * Render a date as a short human-relative string suited for activity feeds:
 * "just now", "5 min ago", "3 hr ago", "Yesterday at 4:19 pm", "Mon at 4:19 pm",
 * "May 19, 2026". Pair with formatAbsoluteTime for tooltips.
 */
export function formatRelativeTime(input: Date | string): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin} min ago`;

  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();

  const time = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  if (sameDay) return `${diffHr} hr ago`;

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate();
  if (isYesterday) return `Yesterday at ${time}`;

  if (diffDay < 7) {
    const day = date.toLocaleDateString(undefined, { weekday: 'short' });
    return `${day} at ${time}`;
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Full absolute representation: "May 19, 2026 at 1:24:07 PM". Used in tooltips
 * where the relative form is shown inline.
 *
 * ⚠️ SECONDS ARE LOAD-BEARING HERE (E9 F-2). One action can now produce several activity rows in the same
 * minute — a final pick emits `PickRecorded` and `PicklistFulfilled` in one transaction — and a picking
 * burst produces many. Without seconds, two rows an instant apart carry an IDENTICAL tooltip, which makes
 * the tooltip useless at exactly the moment someone reaches for it to work out the order of events.
 *
 * Seconds belong in the tooltip and nowhere else: the inline strings stay relative, and adding them there
 * would be noise on every row to serve a question asked about a handful.
 */
export function formatAbsoluteTime(input: Date | string): string {
  const date = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(date.getTime())) return '';

  const datePart = date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
  return `${datePart} at ${timePart}`;
}
